import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import createUser from '@/shared/api/auth/createUser';
import updateUser from '@/shared/api/auth/updateUser';
import useAuth from '@/shared/contexts/auth/hooks/useAuth';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import DialogName from '@/shared/types/dialogName.enum';
import User, { NewUserDetails } from '@/shared/types/user';

const useUserManagement = () => {
  const { t } = useTranslation(['usersManagement', 'common']);
  const redirectLogin = useRedirectLogin();

  const { user: currentUser } = useAuth();

  const { dialog, onUpdateDialogState } = useModalDialog<User, DialogName>();
  const { progressDialog, onUpdateProgressDialog } = useProgressDialog();
  const { notificationBar, onUpdateNotificationBar } = useNotificationBar();

  const [refreshTableIndex, setRefreshTableIndex] = useState(0);

  const handleUpdateUser = async (user: User) => {
    if (currentUser?.email_address === user.email_address && !user.enabled) {
      onUpdateDialogState(DialogName.EnableUser, { open: true });
      return;
    }

    onUpdateProgressDialog({
      title: t('usersManagement:updatingUser'),
      open: true,
      progress: 0,
    });

    const response = await updateUser({
      email: user.email_address,
      data: {
        first_name: user.first_name,
        last_name: user.last_name,
        phone_number: user.phone_number,
        enabled: user.enabled,
      },
    });

    if (response.success) {
      onUpdateDialogState(DialogName.EditUser, { open: false });
      setRefreshTableIndex((prev) => prev + 1);
      onUpdateNotificationBar({
        message: t('usersManagement:userUpdated'),
        severity: 'success',
        open: true,
      });
      onUpdateProgressDialog({ open: false });
    } else {
      onUpdateDialogState(DialogName.EditUser, {
        open: true,
        errors: [response.msg],
      });
      onUpdateProgressDialog({ open: false });
    }
  };

  const handleCreateUser = async (userCreateDetails: NewUserDetails) => {
    onUpdateProgressDialog({
      title: t('usersManagement:creatingUser'),
      open: true,
      progress: 0,
    });

    const response = await createUser({ data: userCreateDetails });

    if (response.success) {
      onUpdateDialogState(DialogName.CreateUser, { open: false });
      onUpdateNotificationBar({
        message: t('usersManagement:userCreated'),
        severity: 'success',
        open: true,
      });
      setRefreshTableIndex((prev) => prev + 1);
      onUpdateProgressDialog({ open: false });
    } else {
      onUpdateDialogState(DialogName.CreateUser, {
        open: true,
        errors: [response.msg],
      });
      onUpdateNotificationBar({
        message: response.msg,
        severity: 'error',
        open: true,
        timeout: 10000,
      });
      onUpdateProgressDialog({ open: false });
    }
  };

  const handleToggleUserEnabled = async (user: User) => {
    const response = await updateUser({
      email: user.email_address,
      data: { enabled: user.enabled },
    });

    if (response.success) {
      setRefreshTableIndex((prev) => prev + 1);
    }
  };

  const handleToggleUserEnabledFromDialog = async (user: User) => {
    onUpdateProgressDialog({
      title: t('usersManagement:disablingUser'),
      open: true,
      progress: 0,
    });

    const response = await updateUser({
      email: user.email_address,
      data: { enabled: user.enabled },
    });

    if (response.success) {
      onUpdateDialogState(DialogName.EnableUser, { open: false });
      onUpdateProgressDialog({ open: false });
      redirectLogin();
    } else {
      onUpdateDialogState(DialogName.EnableUser, {
        open: true,
        errors: [response.msg],
      });
      onUpdateProgressDialog({ open: false });
    }
  };

  return {
    refreshTableIndex,
    dialog,
    progressDialog,
    notificationBar,
    onUpdateDialogState,
    onUpdateProgressDialog,
    onUpdateNotificationBar,
    handleUpdateUser,
    handleCreateUser,
    handleToggleUserEnabled,
    handleToggleUserEnabledFromDialog,
  };
};

export default useUserManagement;
